import React, { useRef, useState } from "react";
import clsx from "clsx";
import { toast } from "sonner";

import PopoverPortal from "./cells/PopoverPortal";
import { payloadForRole } from "./crewRoles";
import RolePicker from "./RolePicker";

/**
 * Slim crew cell for floating-task rows in ROOMS view.
 *
 * No time-window features (no overlap detection, no gap-fill chips, no
 * transfers) because the underlying task has no start/end. Just:
 *   - Click empty cell → role picker → assigns to task.
 *   - Click assigned chip → unassign immediately.
 *
 * Display rule:
 *   - is_lead → "LEAD" text on an amber pill
 *   - any other role → solid dot in a muted pill
 *
 * When the task is later scheduled, the backend migrates the assignment onto
 * the new schedule_block so the schedule view picks up the crew automatically.
 */
export default function FloatingCrewCell({
  crewMember,
  task,
  assignment,
  cellBase,
  rowH,
  onAssignCrewToTask,
  onUnassignCrew,
  onSelect,
  onContextMenu,
  crewRoles,
  onAddCrewRole,
}) {
  const anchorRef = useRef(null);
  const [picker, setPicker] = useState(false);

  const handleClick = async (e) => {
    e.stopPropagation();
    if (assignment) {
      try {
        await onUnassignCrew(assignment.id);
      } catch (err) {
        toast.error("Failed to unassign crew", err);
      }
      return;
    }
    setPicker(true);
  };

  const choose = async (roleKey) => {
    setPicker(false);
    try {
      await onAssignCrewToTask(task.id, crewMember.id, payloadForRole(roleKey));
    } catch (err) {
      toast.error("Failed to assign crew", err);
    }
  };

  return (
    <>
      <div
        ref={anchorRef}
        className={clsx(cellBase, "cursor-pointer hover:bg-amber-glow")}
        style={{ height: rowH, position: "sticky" }}
        onClick={handleClick}
        onContextMenu={onContextMenu}
        data-testid={`floating-crew-${task.id}-${crewMember.id}`}
        title={assignment ? `${crewMember.name} — click to remove` : `Assign ${crewMember.name}`}
      >
        {assignment ? (
          <span
            className={clsx(
              "px-1.5 py-0.5 mono text-2xs uppercase tracking-widest border truncate max-w-full inline-flex items-center gap-1",
              assignment.is_lead
                ? "border-amber text-amber bg-amber-glow"
                : "border-line text-ink-dim"
            )}
          >
            {(crewMember.initials || crewMember.name?.[0] || "?").toUpperCase()}
            {assignment.is_lead ? (
              <span className="ml-0.5 opacity-90">LEAD</span>
            ) : (
              <span aria-hidden style={{ fontSize: "0.95em" }}>●</span>
            )}
          </span>
        ) : (
          <span className="mono text-2xs text-ink-mute uppercase tracking-widest opacity-0 group-hover:opacity-60">·</span>
        )}
      </div>
      <PopoverPortal anchorRef={anchorRef} open={picker} onClose={() => setPicker(false)}>
        <RolePicker
          crewMember={crewMember}
          crewRoles={crewRoles}
          onPick={choose}
          onAddRole={onAddCrewRole}
        />
      </PopoverPortal>
    </>
  );
}
